import { freeze } from "@reduxjs/toolkit"
import autoMergeLevel1 from "redux-persist/lib/stateReconciler/autoMergeLevel1"

/**
 * Drop-in replacement for redux-persist's default `stateReconciler`, for slices written
 * with Redux Toolkit.
 *
 * On REHYDRATE, redux-persist hard-sets the stored values into state (`autoMergeLevel1`
 * assigns them straight onto a plain object) without ever going through Immer - so unlike
 * everything a reducer produces, rehydrated state arrives *unfrozen*, and stays that way
 * until the next `createSlice` reducer runs a produce and deep-freezes it in place.
 *
 * That inconsistency is what breaks consumers that annotate the data they are handed:
 * chart.js patches its array-event hooks (`push`/`pop`/`shift`/`splice`/`unshift`) onto
 * extensible arrays only, then later removes them with `delete array.push` - which throws
 * once Immer has frozen the array in between. It equally hides accidental direct mutation
 * of store data, which silently succeeds while the state is still unfrozen.
 *
 * Deep-freezing the reconciled result gives rehydrated state the same invariant as
 * produced state. `freeze` comes from `@reduxjs/toolkit` so it is the same Immer instance
 * (and the same autoFreeze setting) the slices themselves use.
 *
 * Note the slice's root object is still a plain, unfrozen object - redux-persist rebuilds
 * it on every action anyway (`{ ...newState, _persist }`), so that part already matches the
 * normal, non-rehydrate flow.
 */
export const frozenStateReconciler = (inboundState, originalState, reducedState, config) =>
  freeze(autoMergeLevel1(inboundState, originalState, reducedState, config), true)
